import { AudioHighlightMetadata } from '@modules/feedback/components/AudioType/types';
import {
  generateKeywordSummaryUnitsMap,
  generateReasonSummaryTextMap,
  getKeywordSummaryAudioHighlightMetadata,
  mapAudioHighlightMetadataType,
  mapReasonSummaryHighlightMetadata,
} from '@modules/feedback/components/AudioType/utils';
import { getCombinedMatchedMetadataFromLabels } from '@modules/feedback/components/FeedbackDetails/utils/getCombinedMatchedMetadataFromLabels';
import { PredictionLabelType } from '@modules/feedback/types';
import { FeedbackLabel } from 'v2/core/entities/feedback';
import { AudioConversationMessage } from 'v2/core/entities/feedback/content';
import { SentimentName } from 'v2/core/entities/fields';

export const getAudioHighlightMetadata = (
  selectedLabels: FeedbackLabel[],
  allLabels: FeedbackLabel[],
  conversation: AudioConversationMessage[],
): AudioHighlightMetadata[] => {
  const combinedSelectedLabelsMetadata =
    getCombinedMatchedMetadataFromLabels(selectedLabels);

  const keywordSummaryUnitsMap = generateKeywordSummaryUnitsMap(allLabels);
  const reasonSummaryTextMap = generateReasonSummaryTextMap(
    allLabels,
    keywordSummaryUnitsMap,
    conversation,
  );

  const labelHighlightMetadata: AudioHighlightMetadata[] =
    combinedSelectedLabelsMetadata.reduce(
      (result: AudioHighlightMetadata[], labelMetadata) => {
        if (
          labelMetadata.labelType === PredictionLabelType.Entity &&
          Boolean(labelMetadata.keywordSummary?.units?.length)
        ) {
          return result;
        }

        if (
          labelMetadata.labelType === PredictionLabelType.Reason &&
          Boolean(labelMetadata.keywordSummaryLabelID)
        ) {
          const reasonSummaryMetadata = mapReasonSummaryHighlightMetadata(
            labelMetadata,
            keywordSummaryUnitsMap,
            reasonSummaryTextMap,
            conversation,
          );

          return [
            ...result,
            ...(reasonSummaryMetadata ? [reasonSummaryMetadata] : []),
          ];
        }

        const type = mapAudioHighlightMetadataType(labelMetadata.labelType);

        if (!type) {
          return result;
        }

        const msgIndex = labelMetadata.msgIDx ?? 0;
        const { startTime, endTime } = conversation[msgIndex]?.metadata ?? {};

        return [
          ...result,
          {
            labelID: labelMetadata.id ?? '',
            labelDisplayName: labelMetadata.labelText ?? ' ',
            msgIndex,
            sentiment: labelMetadata.sentiment ?? SentimentName.NEUTRAL,
            startTime,
            endTime,
            type,
          },
        ];
      },
      [],
    );

  const mappedHighlightMetadata = [
    ...labelHighlightMetadata,
    ...getKeywordSummaryAudioHighlightMetadata(selectedLabels, conversation),
  ];

  return [...mappedHighlightMetadata].sort((a, b) => a.startTime - b.startTime);
};
